import { useState, useMemo } from "react";
import { Database } from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState } from "@/components/empty-state";
import { EntityButton } from "@/components/entity-button";
import { TableSkeleton } from "@/components/loading-skeletons";
import { SearchInput } from "@/components/search-input";
import { SortButton } from "@/components/sort-button";
import { RowLink, TableLinkRow } from "@/components/table-link-row";
import { SectionHeader } from "@/components/widgets/section-header";
import { useDataSourcesQuery } from "@/hooks/use-data-sources";
import { formatDate, formatFullDateTime } from "@/lib/utils";

type SortField = "name" | "updated_at";
type SortOrder = "asc" | "desc";

export function DataSourcesPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");

  const { data: dataSources, isLoading, error } = useDataSourcesQuery();

  const filteredAndSortedDataSources = useMemo(() => {
    if (!dataSources) return [];

    let result = dataSources;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter((dataSource) =>
        dataSource.name.toLowerCase().includes(query)
      );
    }

    result = [...result].sort((a, b) => {
      let comparison: number;
      if (sortField === "name") {
        comparison = a.name.localeCompare(b.name);
      } else {
        comparison =
          new Date(a[sortField]).getTime() - new Date(b[sortField]).getTime();
      }
      return sortOrder === "asc" ? comparison : -comparison;
    });

    return result;
  }, [dataSources, searchQuery, sortField, sortOrder]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const getSortDirection = (field: SortField) =>
    sortField === field ? sortOrder : null;

  return (
    <div className="flex flex-1 flex-col gap-4">
      <SectionHeader
        actions={
          dataSources && (
            <EntityButton entityName="Data Source" to="/widgets/data-sources/new" />
          )
        }
      />

      <div className="flex flex-1 flex-col">
        {dataSources && dataSources.length > 0 && (
          <div className="border-b pb-4">
            <SearchInput
              placeholder="Search data sources..."
              value={searchQuery}
              onChange={setSearchQuery}
            />
          </div>
        )}

        {isLoading && <TableSkeleton columns={2} />}

        {error && (
          <p className="py-16 text-center text-sm text-destructive">
            Failed to load data sources
          </p>
        )}

        {dataSources && (
          <>
            {filteredAndSortedDataSources.length === 0 && (
              <EmptyState
                icon={Database}
                title={searchQuery.trim() ? "No results" : "No data sources found"}
                description={
                  searchQuery.trim()
                    ? "No data sources match your search."
                    : "Data sources you add will appear here."
                }
              >
                {!searchQuery.trim() && (
                  <EntityButton
                    entityName="Data Source"
                    to="/widgets/data-sources/new"
                  />
                )}
              </EmptyState>
            )}

            {filteredAndSortedDataSources.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <SortButton
                        label="Name"
                        direction={getSortDirection("name")}
                        onClick={() => handleSort("name")}
                      />
                    </TableHead>
                    <TableHead className="md:w-48">
                      <SortButton
                        label="Updated"
                        direction={getSortDirection("updated_at")}
                        onClick={() => handleSort("updated_at")}
                      />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAndSortedDataSources.map((dataSource) => (
                    <TableLinkRow
                      key={dataSource.id}
                      to={`/widgets/data-sources/${dataSource.id}`}
                    >
                      <TableCell>
                        <RowLink to={`/widgets/data-sources/${dataSource.id}`}>
                          {dataSource.name}
                        </RowLink>
                      </TableCell>
                      <TableCell
                        className="whitespace-nowrap text-muted-foreground"
                        title={formatFullDateTime(dataSource.updated_at)}
                      >
                        {formatDate(dataSource.updated_at)}
                      </TableCell>
                    </TableLinkRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </div>
    </div>
  );
}
